<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import CreateOrderModal from 'dashboard/components/cetakers-flow/CreateOrderModal.vue';

const store = useStore();

const isFetching = ref(false);
const currentPage = ref(1);
const perPage = ref(5);
const selectedOrders = ref([]);
const isSpkModalOpen = ref(false);
const isCreateOrderModalOpen = ref(false);
const isFilterOpen = ref(false);
const selectedInvoiceForSpk = ref(null);

// Filters
const filterDateStart = ref('');
const filterDateEnd = ref('');
const searchQuery = ref(''); // For No Nota / Pelanggan
const filterStatus = ref('');
const filterCs = ref('');
const filterChannel = ref('');

const rawOrders = ref([]);
const rawSpkList = ref([]);
const customerMap = ref({});
const salesChannelsMap = ref({});
const salesChannelsIconMap = ref({});
const salesChannelsList = ref([]);

const stats = computed(() => {
  const isFilterActive = !!(filterDateStart.value || filterDateEnd.value || filterChannel.value || filterStatus.value !== '' || filterCs.value || searchQuery.value);
  
  const todayStandard = (() => {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = now.getFullYear();
    return `${d}-${m}-${y}`;
  })();

  const targetOrders = isFilterActive ? filteredOrders.value : rawOrders.value;

  // Card 1: Pesanan Hari Ini (or Filtered Total if filter is applied)
  const countCard1 = isFilterActive
    ? filteredOrders.value.length
    : rawOrders.value.filter(o => o.nota_tanggal_create && o.nota_tanggal_create.includes(todayStandard)).length;

  // Card 2: SPK Dalam Proses
  const filteredNotaNos = new Set(targetOrders.map(o => o.nota_no).filter(Boolean));
  const activeSpkCount = rawSpkList.value.filter(s => {
    if (isFilterActive && !filteredNotaNos.has(s.nota_no)) return false;
    const status = (s.spk_status_pengerjaan || '').toLowerCase();
    return status !== 'selesai' && status !== 'batal';
  }).length;

  // Card 3: Menunggu Pembayaran
  const waitingPayment = targetOrders.filter(o => String(o.nota_status_pembayaran) !== "1").length;

  // Card 4: Pesanan dapat Diambil
  const readyToPickUp = targetOrders.filter(o => String(o.nota_status_progress) === "100" || (o.nota_status_pengerjaan || '').toLowerCase() === 'selesai').length;

  return [
    {
      title: isFilterActive ? 'Total Pesanan (Filtered)' : 'Pesanan Hari Ini',
      value: countCard1,
      icon: 'i-lucide-shopping-cart',
      color: 'text-n-blue-11',
      bg: 'bg-n-blue-2'
    },
    {
      title: 'SPK Dalam Proses',
      value: activeSpkCount,
      icon: 'i-lucide-settings',
      color: 'text-n-iris-11',
      bg: 'bg-n-iris-2'
    },
    {
      title: 'Menunggu Pembayaran',
      value: waitingPayment,
      icon: 'i-lucide-clock',
      color: 'text-n-amber-11',
      bg: 'bg-n-amber-2'
    },
    {
      title: 'Pesanan dapat Diambil',
      value: readyToPickUp,
      icon: 'i-lucide-package-check',
      color: 'text-n-green-11',
      bg: 'bg-n-green-2'
    },
  ];
});

const filteredOrders = computed(() => {
  return rawOrders.value.filter(o => {
    // Search by No Nota or Pelanggan Name / ID
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase();
      const notaNo = (o.nota_no || '').toLowerCase();
      const custId = o.cust_individual_id ? String(o.cust_individual_id) : (o.cust_corporate_id ? String(o.cust_corporate_id) : '');
      const custName = (customerMap.value[o.cust_individual_id] || '').toLowerCase();

      if (!notaNo.includes(q) && !custId.includes(q) && !custName.includes(q)) {
        return false;
      }
    }
    // Filter by Status Pembayaran
    if (filterStatus.value !== '' && String(o.nota_status_pembayaran) !== String(filterStatus.value)) {
      return false;
    }
    // Filter by CS
    if (filterCs.value && o.nota_officer !== filterCs.value) {
      return false;
    }
    // Filter by Sales Channel
    if (filterChannel.value && String(o.sales_channel_id) !== String(filterChannel.value)) {
      return false;
    }
    
    // Client-side date filtering
    if (filterDateStart.value && filterDateEnd.value) {
      const dateStr = o.nota_tanggal_create ? o.nota_tanggal_create.split(' ')[0] : '';
      if (dateStr) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
          if (isoDate < filterDateStart.value || isoDate > filterDateEnd.value) {
            return false;
          }
        }
      }
    }
    return true;
  });
});

const totalPages = computed(() => Math.ceil(filteredOrders.value.length / perPage.value) || 1);

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  if (current <= 4) {
    return [1, 2, 3, 4, 5, '...', total];
  }
  if (current >= total - 3) {
    return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, '...', current - 1, current, current + 1, '...', total];
});

const paginatedOrders = computed(() => {
  const start = (currentPage.value - 1) * perPage.value;
  return filteredOrders.value.slice(start, start + perPage.value);
});

const uniqueCsList = computed(() => {
  const cs = new Set(rawOrders.value.map(o => o.nota_officer).filter(Boolean));
  return Array.from(cs);
});

const formatRupiah = (number) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(number || 0);
};

const getStatusPembayaran = (status) => {
  return String(status) === "1" ? "Lunas" : "Belum Lunas";
};

const getChannelDisplayName = (order) => {
  if (!order) return null;
  if (typeof order === 'number' || typeof order === 'string') {
    return salesChannelsMap.value[String(order)] || `Channel #${order}`;
  }
  const name = order.channel_name || order.channel?.channel_name || order.sales_channel_name;
  if (name) return name;
  const chId = order.sales_channel_id || order.channel_id || order.channel?.channel_id;
  if (!chId) return null;
  return salesChannelsMap.value[String(chId)] || `Channel #${chId}`;
};

const getChannelIcon = (order) => {
  if (!order) return null;
  const directIcon = order.channel_icon || order.channel?.channel_icon || order.channel?.icon;
  if (directIcon && (directIcon.startsWith('http') || directIcon.startsWith('/') || directIcon.startsWith('data:'))) {
    return directIcon;
  }
  const chId = order.sales_channel_id || order.channel_id || order.channel?.channel_id;
  if (chId && salesChannelsIconMap.value[String(chId)]) {
    const mapIcon = salesChannelsIconMap.value[String(chId)];
    if (mapIcon && (mapIcon.startsWith('http') || mapIcon.startsWith('/') || mapIcon.startsWith('data:'))) {
      return mapIcon;
    }
  }
  return null;
};

const getCustomerDisplayName = (order) => {
  if (!order) return 'Pelanggan Umum';
  const directName = order.customer_name || order.cust_individual_nama || order.cust_name || order.customer?.cust_individual_nama || order.customer?.cust_name || order.pelanggan || order.cust_corporate_nama;
  if (directName) return directName;
  if (order.cust_individual_id && customerMap.value[String(order.cust_individual_id)]) {
    return customerMap.value[String(order.cust_individual_id)];
  }
  if (order.cust_individual_id && customerMap.value[order.cust_individual_id]) {
    return customerMap.value[order.cust_individual_id];
  }
  if (order.cust_individual_id) {
    return `Pelanggan #${order.cust_individual_id}`;
  }
  if (order.cust_corporate_id) {
    return `Perusahaan #${order.cust_corporate_id}`;
  }
  return 'Pelanggan Umum';
};

const openSpkModal = async (order) => {
  selectedInvoiceForSpk.value = order;
  isSpkModalOpen.value = true;
};

const closeSpkModal = () => {
  isSpkModalOpen.value = false;
  selectedInvoiceForSpk.value = null;
};

const toggleSelectAll = (e) => {
  if (e.target.checked) {
    selectedOrders.value = paginatedOrders.value.map(o => o.id);
  } else {
    selectedOrders.value = [];
  }
};

const fetchApiData = async () => {
  isFetching.value = true;
  try {
    const integration = store.getters['integrations/getIntegration']('cetakers_flow');
    if (!integration?.hooks?.length) return;
    
    const settings = integration.hooks[0].settings;
    const apiKey = settings.api_key;
    const endpoint = settings.api_endpoint || 'https://app.cetakers.com/fp-cetakers/api/v1/';
    
    const baseUrl = endpoint.endsWith('/') ? endpoint : `${endpoint}/`;
    
    // 1. Fetch Sales Channels (/channels)
    try {
      const channelRes = await fetch(`${baseUrl}channels`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (channelRes.ok) {
        const channelData = await channelRes.json();
        const chArr = channelData?.data || channelData?.results || [];
        salesChannelsList.value = chArr;
        const map = {};
        const iconMap = {};
        chArr.forEach(ch => {
          if (ch.channel_id) {
            const key = String(ch.channel_id);
            map[key] = ch.channel_name;
            iconMap[key] = ch.channel_icon || ch.icon || ch.channel_logo;
          }
        });
        salesChannelsMap.value = map;
        salesChannelsIconMap.value = iconMap;
      }
    } catch (e) {
      console.warn('Sales channels lookup error:', e);
    }

    // 2. Fetch Invoices (/invoices or /order)
    const dateParams = (filterDateStart.value && filterDateEnd.value)
      ? `&start_date=${filterDateStart.value}&end_date=${filterDateEnd.value}`
      : '';
    const invoiceUrl = `${baseUrl}invoices?limit=200&sort_by=id&order=desc${dateParams}`;
    const response = await fetch(invoiceUrl, {
      headers: { 'Authorization': `Bearer ${apiKey}` }
    });
    
    const resData = await response.json();
    if (resData?.data && Array.isArray(resData.data)) {
      rawOrders.value = [...resData.data].sort((a, b) => (b.id || 0) - (a.id || 0));
    }
    
    // 3. Fetch Customers to map customer names
    try {
      const custUrl = `${baseUrl}customers?limit=200`;
      const custResponse = await fetch(custUrl, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      const custData = await custResponse.json();
      if (custData?.data && Array.isArray(custData.data)) {
        const map = {};
        custData.data.forEach(c => {
          if (c.cust_individual_id) {
            const nameVal = c.cust_individual_nama || c.cust_individual_perusahaan;
            map[String(c.cust_individual_id)] = nameVal;
            map[c.cust_individual_id] = nameVal;
          }
        });
        customerMap.value = map;
      }
    } catch (e) {
      console.warn('Customer lookup error:', e);
    }
    
    // 4. Fetch SPK for aggregation
    const spkUrl = `${baseUrl}spk?limit=200&sort_by=spk_id&order=desc`;
    const spkResponse = await fetch(spkUrl, {
      headers: { 'Authorization': `Bearer ${apiKey}` }
    });
    const spkData = await spkResponse.json();
    if (spkData?.data && Array.isArray(spkData.data)) {
      rawSpkList.value = [...spkData.data].sort((a, b) => (b.spk_id || 0) - (a.spk_id || 0));
    }
    
  } catch (error) {
    console.error('Error fetching Cetakers API:', error);
    useAlert('Gagal mengambil data dari Cetakers Flow API');
  } finally {
    isFetching.value = false;
  }
};

watch([filterDateStart, filterDateEnd], () => {
  fetchApiData();
});

onMounted(() => {
  fetchApiData();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1 p-8">
    <div class="flex flex-col w-full max-w-7xl mx-auto space-y-6">
      
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <img :src="'/dashboard/images/integrations/cetakers_flow.png'" alt="Cetakers Flow" class="size-14 rounded-xl bg-white shadow-sm p-1.5 border border-n-weak" />
          <div class="flex flex-col">
            <h1 class="text-2xl font-bold text-n-slate-12">Dashboard Cetakers Flow</h1>
            <p class="text-n-slate-11 text-sm">Ringkasan aktivitas dan metrik dari Cetakers Flow ERP</p>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <button
            type="button"
            @click="fetchApiData"
            :disabled="isFetching"
            class="h-9 px-3 text-xs font-semibold border border-n-weak rounded-lg bg-white dark:bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1 flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm disabled:opacity-50"
          >
            <Icon icon="i-lucide-refresh-cw" :class="['size-3.5', isFetching ? 'animate-spin' : '']" />
            Refresh
          </button>
          <button
            type="button"
            @click="isCreateOrderModalOpen = true"
            class="h-9 px-3.5 text-xs font-semibold rounded-lg bg-n-brand text-white hover:opacity-90 flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
          >
            <Icon icon="i-lucide-plus" class="size-3.5 text-white" />
            Buat pesanan
          </button>
        </div>
      </div>

      <!-- Stats Grid -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div
          v-for="stat in stats"
          :key="stat.title"
          class="flex flex-col p-5 bg-white dark:bg-n-solid-2 border border-n-weak rounded-2xl shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex justify-between items-center mb-3">
            <span class="text-n-slate-11 text-sm font-medium">{{ stat.title }}</span>
            <div :class="`p-2 rounded-lg ${stat.bg}`">
              <Icon :icon="stat.icon" :class="`size-5 ${stat.color}`" />
            </div>
          </div>
          <span class="text-3xl font-bold text-n-slate-12">{{ stat.value }}</span>
        </div>
      </div>

      <!-- Table Section -->
      <div class="flex flex-col bg-white dark:bg-n-solid-2 border border-n-weak rounded-2xl shadow-sm overflow-hidden mt-4">
        
        <!-- Table Controls Header -->
        <div class="flex items-center justify-between p-4 border-b border-n-weak bg-n-alpha-1/50 relative">
          <h3 class="font-semibold text-n-slate-12 text-base">Data Pesanan</h3>
          
          <div class="flex items-center gap-3 h-9">
            <!-- Search Input (Exactly 36px height) -->
            <div class="relative w-64 h-9 flex items-center">
              <Icon icon="i-lucide-search" class="absolute left-3 size-4 text-n-slate-10 pointer-events-none z-10" />
              <input 
                v-model="searchQuery"
                type="text" 
                placeholder="Cari No Nota / Nama / ID..."
                style="padding-left: 2.25rem !important; padding-right: 0.75rem !important; height: 36px !important; line-height: 36px !important; border-radius: 8px !important; margin: 0 !important;"
                class="!w-full text-xs !bg-white dark:!bg-n-solid-2 !border !border-n-weak focus:!outline-none focus:!ring-2 focus:!ring-n-brand-6 text-n-slate-12 placeholder:!text-n-slate-9 !shadow-sm"
              />
            </div>
            
            <!-- Filter Button (Exactly 36px height) -->
            <div class="relative h-9 flex items-center">
              <button 
                type="button"
                @click="isFilterOpen = !isFilterOpen"
                style="height: 36px !important; border-radius: 8px !important; margin: 0 !important;"
                :class="[
                  'px-3 text-xs font-semibold border flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm',
                  isFilterOpen 
                    ? 'bg-n-brand-2 text-n-brand-11 border-n-brand-4' 
                    : 'bg-white dark:bg-n-solid-2 text-n-slate-12 border-n-weak hover:bg-n-alpha-1'
                ]"
              >
                <Icon icon="i-lucide-filter" class="size-3.5" />
                Filter
              </button>
              
              <!-- Floating Filter Settings (Popover) -->
              <div v-if="isFilterOpen" class="absolute top-full right-0 mt-2 w-max p-5 bg-white dark:bg-n-solid-1 border border-n-weak rounded-xl shadow-xl z-20 flex flex-col gap-4 min-w-[300px]">
                <div class="flex items-center justify-between mb-1">
                  <span class="font-semibold text-sm text-n-slate-12">Filter Pesanan</span>
                  <button @click="isFilterOpen = false" class="text-n-slate-10 hover:text-n-slate-12">
                     <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                  </button>
                </div>
                <!-- Date Filters -->
                <div class="flex items-center gap-2">
                  <div class="flex flex-col w-full">
                    <span class="text-xs text-n-slate-11 mb-1">Dari Tanggal</span>
                    <input type="date" v-model="filterDateStart" class="px-3 py-1.5 text-sm bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 w-full" />
                  </div>
                  <span class="text-n-slate-10 mt-5">-</span>
                  <div class="flex flex-col w-full">
                    <span class="text-xs text-n-slate-11 mb-1">Sampai Tanggal</span>
                    <input type="date" v-model="filterDateEnd" class="px-3 py-1.5 text-sm bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 w-full" />
                  </div>
                </div>

                <!-- Sales Channel Filter -->
                <div class="flex flex-col">
                  <span class="text-xs text-n-slate-11 mb-1">Sales Channel</span>
                  <select v-model="filterChannel" class="py-1.5 px-3 text-sm bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12">
                    <option value="">Semua Sales Channel</option>
                    <option v-for="ch in salesChannelsList" :key="ch.channel_id" :value="ch.channel_id">
                      {{ ch.channel_name }}
                    </option>
                  </select>
                </div>
                
                <div class="flex flex-col">
                  <span class="text-xs text-n-slate-11 mb-1">Status Pembayaran</span>
                  <select v-model="filterStatus" class="py-1.5 px-3 text-sm bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12">
                    <option value="">Semua Status</option>
                    <option value="1">Lunas</option>
                    <option value="0">Belum Lunas</option>
                  </select>
                </div>
                
                <div class="flex flex-col">
                  <span class="text-xs text-n-slate-11 mb-1">Akun CS (Dibuat Oleh)</span>
                  <select v-model="filterCs" class="py-1.5 px-3 text-sm bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12">
                    <option value="">Semua Akun CS</option>
                    <option v-for="cs in uniqueCsList" :key="cs" :value="cs">{{ cs }}</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div class="w-px h-5 bg-n-weak my-auto"></div>
            
            <!-- Baris Select (Exactly 36px height) -->
            <div class="flex items-center gap-2 h-9">
              <span class="text-xs font-medium text-n-slate-11 shrink-0">Baris:</span>
              <select 
                v-model="perPage" 
                style="height: 36px !important; padding-top: 0 !important; padding-bottom: 0 !important; padding-left: 0.625rem !important; padding-right: 1.75rem !important; border-radius: 8px !important; margin: 0 !important; line-height: 36px !important;"
                class="text-xs bg-white dark:bg-n-solid-2 border border-n-weak focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 shadow-sm font-medium cursor-pointer"
              >
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto min-h-[300px]">
          <table class="w-full text-left text-sm whitespace-nowrap relative">
            <thead class="bg-n-alpha-1 text-n-slate-11 border-b border-n-weak uppercase text-xs font-semibold">
              <tr>
                <th class="p-4 w-12 text-center">
                  <input type="checkbox" class="rounded border-n-weak text-n-brand-9 focus:ring-n-brand-6" @change="toggleSelectAll" />
                </th>
                <th class="p-4">Pesanan & Channel</th>
                <th class="p-4">Tanggal Nota</th>
                <th class="p-4">Pelanggan</th>
                <th class="p-4 text-right">Jumlah Pesanan</th>
                <th class="p-4">Dibuat Oleh</th>
                <th class="p-4 text-center">Status</th>
              </tr>
            </thead>
            
            <tbody class="divide-y divide-n-weak text-n-slate-12">
              <!-- Loading State -->
              <tr v-if="isFetching">
                <td colspan="7" class="p-12 text-center">
                  <div class="flex flex-col items-center gap-3">
                    <Icon icon="i-lucide-loader-2" class="size-8 text-n-brand-9 animate-spin" />
                    <span class="text-n-slate-11 font-medium">Mengambil data pesanan...</span>
                  </div>
                </td>
              </tr>
              
              <!-- Empty State -->
              <tr v-else-if="paginatedOrders.length === 0">
                <td colspan="7" class="p-12 text-center">
                  <div class="flex flex-col items-center gap-2">
                    <Icon icon="i-lucide-inbox" class="size-10 text-n-slate-8" />
                    <span class="text-n-slate-11 font-medium text-base mt-2">Tidak ada data pesanan ditemukan.</span>
                    <span class="text-n-slate-10 text-sm">Coba sesuaikan filter rentang tanggal atau pencarian Anda.</span>
                  </div>
                </td>
              </tr>
              
              <!-- Data Rows -->
              <tr v-for="order in paginatedOrders" :key="order.id" class="hover:bg-n-alpha-1 transition-colors" v-else>
                <td class="p-4 text-center">
                  <input type="checkbox" :value="order.id" v-model="selectedOrders" class="rounded border-n-weak text-n-brand-9 focus:ring-n-brand-6" />
                </td>
                <td class="p-4">
                  <div class="flex flex-col gap-1">
                    <span class="font-bold text-n-brand-11 text-sm tracking-tight">{{ order.nota_no }}</span>
                    <div class="flex items-center gap-1.5 flex-wrap">
                      <!-- Order Type Badge -->
                      <span :class="[
                        'px-2 py-0.5 text-[10px] font-semibold rounded-md inline-flex items-center gap-1 uppercase',
                        order.nota_order_type === 'online' ? 'bg-sky-500/10 text-sky-700 dark:text-sky-400' :
                        order.nota_order_type === 'marketplace' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400' :
                        'bg-slate-500/10 text-slate-700 dark:text-slate-400'
                      ]">
                        <Icon :icon="order.nota_order_type === 'online' ? 'i-lucide-globe' : (order.nota_order_type === 'marketplace' ? 'i-lucide-shopping-bag' : 'i-lucide-store')" class="size-3" />
                        {{ order.nota_order_type || 'Walk-in' }}
                      </span>

                      <!-- Sales Channel Badge -->
                      <span v-if="getChannelDisplayName(order)" class="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-blue-500/10 text-blue-700 dark:text-blue-400 inline-flex items-center gap-1">
                        <img v-if="getChannelIcon(order)" :src="getChannelIcon(order)" class="size-3.5 object-contain rounded-xs" />
                        <Icon v-else icon="i-lucide-store" class="size-3 text-blue-600 dark:text-blue-400" />
                        {{ getChannelDisplayName(order) }}
                      </span>
                    </div>
                    <span v-if="order.nota_marketplace_no" class="text-xs text-n-slate-10 font-mono">
                      MP: {{ order.nota_marketplace_no }}
                    </span>
                  </div>
                </td>
                <td class="p-4 text-n-slate-11 font-medium">{{ order.nota_tanggal_create }}</td>
                <td class="p-4">
                  <div class="flex items-center gap-2.5">
                    <div class="size-7 rounded-full bg-n-brand/10 text-n-brand-11 flex items-center justify-center text-xs font-bold uppercase shrink-0">
                      {{ getCustomerDisplayName(order).charAt(0) }}
                    </div>
                    <div class="flex flex-col min-w-0">
                      <span class="font-medium text-n-slate-12 truncate max-w-[180px]" :title="getCustomerDisplayName(order)">
                        {{ getCustomerDisplayName(order) }}
                      </span>
                      <span class="text-[11px] text-n-slate-10" v-if="order.cust_individual_id || order.cust_corporate_id">
                        ID: {{ order.cust_individual_id || order.cust_corporate_id }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="p-4 text-right">
                  <button @click="openSpkModal(order)" class="font-semibold text-n-slate-12 hover:text-n-brand-11 flex items-center justify-end gap-1 w-full transition-colors group">
                    <Icon icon="i-lucide-receipt" class="size-3.5 opacity-0 group-hover:opacity-100 text-n-brand-9 transition-opacity" />
                    {{ formatRupiah(order.nota_total_harga || order.nota_total) }}
                  </button>
                </td>
                <td class="p-4">
                  <div class="flex items-center gap-2">
                    <div class="size-6 rounded-full bg-n-slate-3 flex items-center justify-center text-xs font-bold text-n-slate-11 uppercase">
                      {{ (order.nota_officer || 'U').charAt(0) }}
                    </div>
                    <span class="capitalize font-medium text-n-slate-11">{{ order.nota_officer || 'Unknown' }}</span>
                  </div>
                </td>
                <td class="p-4 text-center">
                  <span
                    class="px-2.5 py-0.5 text-xs font-semibold rounded-full inline-flex items-center gap-1.5 text-white shadow-2xs cursor-default"
                    :style="String(order.nota_status_pembayaran) === '1' 
                      ? 'background-color: #10b981 !important; color: #ffffff !important;' 
                      : 'background-color: #ef4444 !important; color: #ffffff !important;'"
                  >
                    <span class="size-1.5 rounded-full bg-white"></span>
                    {{ getStatusPembayaran(order.nota_status_pembayaran) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination Footer -->
        <div class="flex items-center justify-between p-4 border-t border-n-weak bg-n-alpha-1/50">
          <span class="text-sm text-n-slate-11">
            Menampilkan {{ filteredOrders.length === 0 ? 0 : ((currentPage - 1) * perPage) + 1 }} - 
            {{ Math.min(currentPage * perPage, filteredOrders.length) }} dari {{ filteredOrders.length }} pesanan
          </span>
          
          <div class="flex items-center gap-1">
            <button 
              type="button"
              @click="currentPage > 1 && currentPage--" 
              :disabled="currentPage === 1"
              class="p-1.5 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <Icon icon="i-lucide-chevron-left" class="size-5" />
            </button>
            
            <div class="flex items-center gap-1 mx-2" v-if="totalPages > 0">
              <template v-for="(item, index) in visiblePages" :key="index">
                <span v-if="item === '...'" class="px-2 text-xs text-n-slate-10 select-none">...</span>
                <button 
                  v-else
                  type="button"
                  @click="currentPage = item"
                  :style="currentPage === item 
                    ? 'background-color: #2563eb !important; color: #ffffff !important; border: 1px solid #2563eb !important;' 
                    : 'background-color: transparent !important; color: #64748b !important; border: 1px solid transparent !important;'"
                  class="size-8 rounded-lg text-xs font-semibold flex items-center justify-center cursor-pointer transition-all hover:bg-n-alpha-2"
                >
                  {{ item }}
                </button>
              </template>
            </div>
            
            <button 
              type="button"
              @click="currentPage < totalPages && currentPage++" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="p-1.5 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <Icon icon="i-lucide-chevron-right" class="size-5" />
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Create Order Modal -->
    <CreateOrderModal
      :show="isCreateOrderModalOpen"
      @close="isCreateOrderModalOpen = false"
      @success="fetchApiData"
    />

    <!-- SPK Detail Modal -->
    <div v-if="isSpkModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" @click.self="closeSpkModal">
      <div class="bg-white dark:bg-n-solid-1 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-n-weak transform transition-all">
        <div class="flex items-center justify-between p-4 border-b border-n-weak">
          <h3 class="font-bold text-n-slate-12">Detail SPK - {{ selectedInvoiceForSpk?.nota_no }}</h3>
          <button @click="closeSpkModal" class="p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded">
            <Icon icon="i-lucide-x" class="size-5" />
          </button>
        </div>
        <div class="p-4 max-h-[60vh] overflow-y-auto">
          <ul class="space-y-3" v-if="rawSpkList.filter(s => s.nota_no === selectedInvoiceForSpk?.nota_no).length > 0">
            <li v-for="spk in rawSpkList.filter(s => s.nota_no === selectedInvoiceForSpk?.nota_no)" :key="spk.spk_id" class="flex flex-col gap-2 p-3 bg-n-surface-2 rounded-xl border border-n-weak/50">
              <div class="flex justify-between items-start">
                <span class="font-semibold text-n-slate-12">{{ spk.spk_judul }}</span>
                <span class="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-n-brand-2 text-n-brand-11 border border-n-brand-4">
                  {{ spk.spk_status_pengerjaan }}
                </span>
              </div>
              <div class="flex items-center gap-4 text-xs text-n-slate-11">
                <span><strong class="text-n-slate-12">{{ spk.spk_jumlah }}</strong> {{ spk.spk_satuan }}</span>
                <span>Ukuran: {{ spk.spk_ukuran }}</span>
              </div>
            </li>
          </ul>
          <div v-else class="text-center flex flex-col items-center justify-center py-8">
             <Icon icon="i-lucide-file-x" class="size-8 text-n-slate-8 mb-2" />
             <span class="text-sm text-n-slate-11">Detail SPK belum tersedia atau belum difetch.</span>
          </div>
        </div>
        <div class="p-4 border-t border-n-weak flex justify-between items-center bg-n-surface-1">
          <span class="text-xs text-n-slate-10">Total: {{ formatRupiah(selectedInvoiceForSpk?.nota_total_harga || selectedInvoiceForSpk?.nota_total || 0) }}</span>
          <Button color="slate" variant="ghost" @click="closeSpkModal">Tutup</Button>
        </div>
      </div>
    </div>
  </div>
</template>
